import { ChangeDetectionStrategy, Component, computed, inject, Injector, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonDateTimeField, CommonDateTimeFieldConfig, CommonDateTimeFieldWidget, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonSelectFieldWidget, CommonTextField, CommonTextFieldWidget } from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService } from '@eforall/dashboard';
import { DialogMode, EngagementExternalRole, EngagementExternalRoleId, EngagementFollowUpForm, EngagementFollowUpFormValue, StaffData, StaffRole, StaffRoleId } from '@interfaces';
import { AppService, DomainDataService } from '../../../../../services';
import { FollowUpDialogService, PersonFinderWidget, StaffSelectorWidget } from '../../../../widget';
import { EngagementViewerDialogService } from '../../engagement-viewer/engagement-viewer.service';

export type FollowUpEditorDialogData = | { mode: DialogMode.ADD; staffRoleId?: StaffRoleId, externalRoleId?: EngagementExternalRoleId, engagementId?: number, followUpId?: number, } | { mode: DialogMode.EDIT; followUpId: number; };


@Component({
	selector: 'follow-up-editor-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		CommonTextFieldWidget,
		CommonSelectFieldWidget,
		CommonDateTimeFieldWidget,
		StaffSelectorWidget,
		PersonFinderWidget,
	],
	templateUrl: './follow-up-editor.dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styles: [``]
})
export class FollowUpEditorDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<FollowUpEditorDialog>);
	public dialogContext: DialogContext<FollowUpEditorDialogData> = inject(MAT_DIALOG_DATA);

	private readonly domainData = inject(DomainDataService);
	private readonly dialog = inject(DialogService);
	public readonly app = inject(AppService);
	private readonly engagementService = inject(EngagementViewerDialogService);
	private readonly service = inject(FollowUpDialogService);

	private readonly form = new CommonForm(inject(Injector));
	public readonly showFollowUpForm = signal<boolean>(false);

	public readonly headerTitle = computed(() => {
		const data = this.dialogContext.data;
		if (data?.mode == DialogMode.ADD) return 'Add Follow-up';
		if (data?.mode == DialogMode.EDIT) return 'Edit Follow-up';
		return 'View Follow-up';
	});

	public personSearchDisabled = signal<boolean>(false);
	public hasPersonFieldError: boolean = false;
	public staffData = signal<StaffData[]>([]);

	public values = signal<EngagementFollowUpFormValue>({
		personId: 0,
		personName: '',
		externalRoleId: EngagementExternalRoleId.Entrepreneur,
		dueByUTC: Date.now() / 1000,
		staffId: 0,
		staffRoleId: StaffRoleId.EnrollmentCoordinator,
		purposeId: 0,
		note: '',
	});


	private readonly externalRolesOptions = computed(() => [
		{ value: EngagementExternalRoleId.Entrepreneur, text: EngagementExternalRole.Entrepreneur },
		{ value: EngagementExternalRoleId.Volunteer, text: EngagementExternalRole.Volunteer },
		{ value: EngagementExternalRoleId.Partner, text: EngagementExternalRole.Partner },
	]);
	readonly externalRoleId = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'External Role', options: this.externalRolesOptions(), isRequired: true, isSpanish: false, isEnabled: signal(true)
		}),
		actualValue: computed(() => this.values().externalRoleId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.values();
			this.values.set({ ...values, externalRoleId: value });
		},
		error: signal(''),
		formFieldName: 'external-role',
		form: this.form,
	}));

	readonly followUpDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({ label: 'Follow-up Date', isRequired: true, isSpanish: false, isEnabled: signal(true), type: 'event', includeTime: false }),
		actualValue: computed(() => this.values().dueByUTC),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		individualSave: async (value: number | undefined) => {
			const values = this.values();
			this.values.set({ ...values, dueByUTC: value || 0 });
		},
		error: signal(''),
		formFieldName: 'follow-up-date',
		form: this.form,
	}));

	private readonly staffRoleOptions = computed(() => [
		{ value: StaffRoleId.ProgramExperience, text: StaffRole.ProgramExperience },
		{ value: StaffRoleId.VolunteerRecruiter, text: StaffRole.VolunteerRecruiter },
		{ value: StaffRoleId.VolunteerCoordinator, text: StaffRole.VolunteerCoordinator },
		{ value: StaffRoleId.EnrollmentCoordinator, text: StaffRole.EnrollmentCoordinator },
		{ value: StaffRoleId.ProgramFacilitator, text: StaffRole.ProgramFacilitator },
	]);
	readonly staffRoleId = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Staff Role', options: this.staffRoleOptions(), isRequired: true, isSpanish: false, isEnabled: signal(true)
		}),
		actualValue: computed(() => this.values().staffRoleId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.values();
			this.values.set({ ...values, staffRoleId: value });
		},
		error: signal(''),
		formFieldName: 'staff-role',
		form: this.form,
	}));

	private readonly purposeOptions = computed(() => {
		const domainData = this.domainData.data();
		return domainData.engagementPurposes.map(purpose =>
			({ text: purpose.name, value: purpose.purposeId })
		).sort((a, b) => a.text < b.text ? -1 : 1);
	});
	readonly purpose = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Purpose', options: this.purposeOptions(), isRequired: true, isSpanish: false, isEnabled: signal(true)
		}),
		actualValue: computed(() => this.values().purposeId || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.values();
			this.values.set({ ...values, purposeId: value });
		},
		error: signal(''),
		formFieldName: 'purpose',
		form: this.form,
	}));

	readonly notes = computed<CommonTextField>(() => ({
		config: signal({ label: 'Notes', min: 1, max: 2000, fixCase: 'sentence-case', isRequired: true, multiLine: true, isSpanish: false, isEnabled: signal(true) }),
		actualValue: computed(() => this.values().note || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		individualSave: async (value: string) => {
			const values = this.values();
			this.values.set({ ...values, note: value });
		},
		error: signal(''),
		formFieldName: 'notes',
		form: this.form,
	}));


	async ngOnInit() {
		this.staffData.set(this.app.data().staff);
		const data = this.dialogContext.data;

		if (data?.mode === DialogMode.ADD && data.staffRoleId)
			this.values.set({ ...this.values(), staffRoleId: data.staffRoleId });

		if (data?.mode === DialogMode.ADD && data.externalRoleId)
			this.values.set({ ...this.values(), externalRoleId: data.externalRoleId });

		if (data?.mode === DialogMode.ADD) {
			const engagementDetails = this.engagementService.engagementDetails();
			if (engagementDetails && engagementDetails.engagementId && data?.engagementId == engagementDetails.engagementId) {
				this.values.set({
					...this.values(),
					personId: engagementDetails.person.id,
					personName: engagementDetails.person.fullName,
					externalRoleId: engagementDetails.externalRoleId,
					staffId: engagementDetails.lastContactStaffId!,
					staffRoleId: engagementDetails.staffRoleId,
					purposeId: engagementDetails.purposeId,
				})
				this.personSearchDisabled.set(true);
			}
		} else if (data?.mode === DialogMode.EDIT) {
			await this.onRevertChanges();
			this.personSearchDisabled.set(true);
		}
		this.showFollowUpForm.set(true);

	}

	setSelectedPersonId(id: number) {
		const values = this.values();
		this.values.set({ ...values, personId: id });
	}

	handleStaffValueChange = (value: { staffId?: number } | undefined) => {
		if (value?.staffId) {
			const values = this.values();
			this.values.set({ ...values, staffId: value?.staffId });
		}
	};

	public actions = computed<DialogAction[]>(() => {
		const data = this.dialogContext.data;
		if (!data) return [];

		const actions: DialogAction[] = [
			{
				id: 'addFormBtn',
				enabled: signal(true),
				visible: signal(true),
				text: computed(() => data.mode == DialogMode.ADD ? 'Add' : 'Update'),
				linkType: "callback",
				cssClass: signal('btn-success'),
				willCloseDialog: false,
				callback: async () => this.onSaveFollowUp()
			},
		];
		if (data.mode === DialogMode.EDIT) {
			actions.push({
				id: 'revertChanges',
				enabled: signal(true),
				visible: signal(true),
				cssClass: signal('btn-standard'),
				text: signal('Revert Changes'),
				linkType: 'callback',
				willCloseDialog: false,
				callback: async () => await this.onRevertChanges()
			});
		}
		actions.push({
			id: 'no',
			enabled: signal(true),
			visible: signal(true),
			cssClass: signal('btn-standard'),
			text: signal('Cancel'),
			linkType: "no-action",
			willCloseDialog: true,
		});
		return actions;
	});

	private async onRevertChanges() {
		const data = this.dialogContext.data;
		if (!data) return;
		const followUp = await this.service.getFollowUpById(data.followUpId!); // revert btn is available only in edit mode, so followUpId is guaranteed to be present
		this.values.set({
			personId: followUp.personId,
			personName: followUp.personName,
			externalRoleId: followUp.externalRoleId,
			dueByUTC: followUp.dueByUTC,
			staffId: followUp.staffId,
			staffRoleId: followUp.staffRoleId,
			purposeId: followUp.purposeId,
			note: followUp.note
		});
	}

	private checkIfValid() {
		const values = this.values();
		return !!values.personId
			&& !!values.staffId
			&& !!values.externalRoleId
			&& !!values.dueByUTC
			&& !!values.staffRoleId
			&& !!values.purposeId
			&& !!values.note
			&& !this.externalRoleId().error()
			&& !this.followUpDate().error()
			&& !this.staffRoleId().error()
			&& !this.purpose().error()
			&& !this.notes().error();
	}

	private async onSaveFollowUp() {
		const data = this.dialogContext.data;
		if (data?.mode === DialogMode.ADD) {
			await this.handleFollowUp('add');
		} else if (data?.mode === DialogMode.EDIT) {
			await this.handleFollowUp('update');
		}
	}


	private async handleFollowUp(action: 'add' | 'update') {
		const data = this.dialogContext.data;
		if (!data) return;

		if (this.checkIfValid()) {

			const payload: EngagementFollowUpForm = {
				personId: this.values().personId,
				externalRoleId: this.values().externalRoleId,
				staffId: this.values().staffId,
				staffRoleId: this.values().staffRoleId,
				purposeId: this.values().purposeId,
				note: this.values().note,
				dueByUTC: this.values().dueByUTC,
			};

			let followUpId: number | undefined;
			if (action === 'add' && data.mode === DialogMode.ADD) {
				followUpId = await this.service.addFollowUp(payload, data.engagementId);
			} else if (action === 'update' && data.mode === DialogMode.EDIT) {
				followUpId = await this.service.updateFollowUp(data.followUpId, payload);
			}

			this.closeDialog(action, followUpId);
		} else {
			console.error('Invalid form, please fill out all required fields');
			this.hasPersonFieldError = !this.values().personId;
			this.form.resetAll(true);
			this.dialog.showMessage('Invalid Form', 'Invalid form please fill out all required fields.', 'Ok', [], 400, 200);
		}
	}

	public async closeDialog(action: 'add' | 'update', followUpId?: number) {
		// psuedo action to pass back the created/updated folowUp id to the caller
		const dialogAction: DialogAction = {
			id: action,
			enabled: signal(true),
			visible: signal(true),
			text: signal('Add'),
			cssClass: signal('btn-standard'),
			linkType: "no-action",
			callbackResult: followUpId,
			willCloseDialog: true,
		}
		this.dialogRef.close(dialogAction);
	}
}